import { useState } from "react"
import { Button } from "./Button"
import { Tabs } from "./Tabs"
import { useBalance } from "~/hooks/useBalance"
import { TOKENS } from "~/constants"
import { shortAddress } from "~/utils/shortAddress"
import { NumericalInput } from "./NumericalInput"
import { useDagProgram } from "~/hooks/useDagProgram"
import { lamportsToSol, uiAmountToLamports } from "~/utils/conversion"
import { useQuote } from "~/hooks/useQuote"
import { useVotingPower } from "~/hooks/useVotingPower"
import { GovenModal } from "~/modals/GovenModal"
import { createPortal } from "react-dom"
import { BN } from "@coral-xyz/anchor"
import type { GameData } from "~/hooks/useGameData"
import { TOKEN_2022_PROGRAM_ID } from "@solana/spl-token"
import { PublicKey } from "@solana/web3.js"

// Import TokenInfo type from constants
type TokenInfo = typeof TOKENS[keyof typeof TOKENS]

export const TokenPanel = ({ game }: { game: GameData }) => {
  // Convert game.token to match TokenInfo structure
  const token: TokenInfo = {
    address: game.token.address,
    symbol: game.token.symbol,
    name: game.token.name,
    price: game.token.price,
    img: game.token.img,
    decimals: game.token.decimals,
    program: TOKEN_2022_PROGRAM_ID
  } as TokenInfo // Type assertion since we know the structure matches

  return (
    <div className="flex flex-col gap-6">
      <Info game={game} token={token} />
      <BuyOrSell token={token} />
      <StakeOrUnstake token={token} />
    </div>
  )
}

const Info = ({ game, token }: { game: GameData, token: TokenInfo }) => {
  return (
    <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
      <div className="flex items-center relative gap-3">
        <img className="w-10 h-10 rounded-[5.71px]" src={token.img} />
        <div className="flex flex-col items-start flex-grow gap-0.5">
          <div className="flex items-center relative gap-1">
            <p className="text-base font-semibold text-left text-white">
              {token.symbol}
            </p>
            <p className="text-xs font-medium text-left text-[#6b6d6e]">
              ({token.name})
            </p>
          </div>
          <div className="flex relative gap-1">
            <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
              {shortAddress(token.address)}
            </p>
            <svg
              width={16}
              height={16}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 relative"
              preserveAspectRatio="none"
            >
              <path
                d="M13.3335 9.41666L13.3335 4.6666C13.3335 3.56202 12.4381 2.66659 11.3335 2.6666L6.5835 2.66666M9.3335 13.3333L4.16683 13.3333C3.3384 13.3333 2.66683 12.6618 2.66683 11.8333L2.66683 6.66666C2.66683 5.83823 3.3384 5.16666 4.16683 5.16666L9.3335 5.16666C10.1619 5.16666 10.8335 5.83823 10.8335 6.66666L10.8335 11.8333C10.8335 12.6618 10.1619 13.3333 9.3335 13.3333Z"
                stroke="#6B6D6E"
                strokeWidth="1.33333"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
            Market Cap
          </p>
          <p className="text-base font-semibold text-left text-white">
            {game.token.marketCap}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
            FDV
          </p>
          <p className="text-base font-semibold text-left text-white">
            {game.token.fdv}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
            Liquidity
          </p>
          <p className="text-base font-semibold text-left text-white">
            {game.token.liquidity}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-[13px] font-medium text-left text-[#6b6d6e]">
            Transactions
          </p>
          <p className="text-base font-semibold text-left text-white">
            {game.token.transactions}
          </p>
        </div>
      </div>
    </div>
  )
}

const BuyOrSell = ({ token }: { token: TokenInfo }) => {
  const { getBalance, mutate: mutateBalance } = useBalance()
  const { buy, sell, isPending } = useDagProgram()

  const [tab, setTab] = useState("buy")
  const [payAmount, setPayAmount] = useState("")

  const payToken = tab === "buy" ? TOKENS.PLATFORM : token
  const receiveToken = tab === "buy" ? token : TOKENS.PLATFORM

  const { tokenPrice, receiveAmount, isLoading, mutate: mutateQuote } = useQuote({
    payToken,
    receiveToken,
    uiAmount: new BN(payAmount || "0"),
  })

  const handleMax = () => {
    setPayAmount(getBalance(payToken.address).toString())
  }

  const handleConfirm = async () => {
    if (tab === "buy") {
      await buy({
        token,
        tokenAmount: receiveAmount!,
        slippage: 0.01,
      })
    } else {
      await sell({
        token,
        tokenAmount: new BN(payAmount),
        slippage: 0.01,
      })
    }
    mutateBalance()
    mutateQuote()
  }

  const formattedReceiveAmount = receiveAmount
    ? lamportsToSol(receiveAmount, receiveToken.decimals)
    : "0"

  return (
    <div className="flex flex-col gap-4">
      <Tabs
        options={[
          { label: "Buy", value: "buy" },
          { label: "Sell", value: "sell" },
        ]}
        value={tab}
        onChange={setTab}
      />
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex items-center relative gap-4">
            <p className="flex-1 text-[13px] font-medium text-left text-[#6b6d6e]">
              Pay
            </p>
            <p className="text-xs font-medium text-left text-[#6b6d6e]">
              {getBalance(payToken.address)} {payToken.symbol}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1 flex flex-col relative gap-0.5">
              <NumericalInput
                value={payAmount}
                onUserInput={setPayAmount}
                placeholder="0.00"
                className="w-full text-lg font-semibold text-left text-[#fff] bg-transparent border-none outline-none"
              />
            </div>
            <button
              onClick={handleMax}
              className="flex items-center gap-3 px-3 py-2 rounded-[100px] bg-[#212425]"
            >
              <div className="flex items-center relative gap-1.5">
                <img className="w-4 h-4" src={payToken.img} />
                <p className="text-sm font-semibold text-left text-white">
                  {payToken.symbol}
                </p>
              </div>
              <p className="text-sm font-semibold text-left text-[#6b6d6e]">MAX</p>
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex items-center relative gap-4">
            <p className="flex-1 text-[13px] font-medium text-left text-[#6b6d6e]">
              Receive
            </p>
            <p className="text-xs font-medium text-left text-[#6b6d6e]">
              {getBalance(receiveToken.address)} {receiveToken.symbol}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1 flex flex-col relative gap-0.5">
              <p className="w-full text-lg font-semibold text-left text-[#fff] min-h-[28px]">
                {formattedReceiveAmount}
              </p>
            </div>
            <button className="flex items-center gap-3 px-3 py-2 rounded-[100px] bg-[#212425]">
              <div className="flex items-center relative gap-1.5">
                <img className="w-4 h-4" src={receiveToken.img} />
                <p className="text-sm font-semibold text-left text-white">
                  {receiveToken.symbol}
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center relative gap-2.5 p-1">
        <p className="flex-grow w-[262px] text-xs text-left text-[#6b6d6e]">
          {tab === "buy" ? (
            <>
              1 {payToken.symbol} ≈ {tokenPrice} {receiveToken.symbol}
            </>
          ) : (
            <>
              1 {payToken.symbol} ≈ {1 / tokenPrice} {receiveToken.symbol}
            </>
          )}
        </p>
      </div>

      <Button
        loading={isPending}
        disabled={isLoading || isPending || !payAmount || receiveAmount?.isZero()}
        type={tab === "buy" ? "primary" : "secondary"}
        onClick={handleConfirm}
      >
        {tab === "buy" ? "Buy" : "Sell"}
      </Button>
    </div>
  )
}

const StakeOrUnstake = ({ token }: { token: TokenInfo }) => {
  const { getBalance } = useBalance()
  const { lock, isPending } = useDagProgram()
  const [isOpen, setIsOpen] = useState(false)
  const [amount, setAmount] = useState("")
  const [epoch, setEpoch] = useState(1)

  const { currentVotingPower, totalVotingPower, isLoading, mutate: mutateVotingPower } = useVotingPower({
    token,
    uiAmount: new BN(amount || "0"),
  })

  const handleMax = () => {
    setAmount(getBalance(token.address).toString())
  }

  const handleConfirm = async () => {
    await lock({
      token,
      tokenAmount: new BN(amount),
      epoch,
    })
    mutateVotingPower()
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex items-center relative gap-4">
            <p className="flex-1 text-[13px] font-medium text-left text-[#6b6d6e]">
              Stake
            </p>
            <p className="text-xs font-medium text-left text-[#6b6d6e]">
              {getBalance(token.address)} {token.symbol}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1 flex flex-col relative gap-0.5">
              <NumericalInput
                value={amount}
                onUserInput={setAmount}
                placeholder="0.00"
                className="w-full text-lg font-semibold text-left text-[#fff] bg-transparent border-none outline-none"
              />
            </div>
            <button
              onClick={handleMax}
              className="flex items-center gap-3 px-3 py-2 rounded-[100px] bg-[#212425]"
            >
              <div className="flex items-center relative gap-1.5">
                <img className="w-4 h-4" src={token.img} />
                <p className="text-sm font-semibold text-left text-white">
                  {token.symbol}
                </p>
              </div>
              <p className="text-sm font-semibold text-left text-[#6b6d6e]">MAX</p>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex items-center relative gap-4">
            <p className="flex-1 text-[13px] font-medium text-left text-[#6b6d6e]">
              Lock Duration
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1 flex flex-col relative gap-0.5">
              <NumericalInput
                value={epoch.toString()}
                onUserInput={(value: string) => setEpoch(parseInt(value) || 1)}
                placeholder="1"
                className="w-full text-lg font-semibold text-left text-[#fff] bg-transparent border-none outline-none"
              />
            </div>
            <button className="flex items-center gap-3 px-3 py-2 rounded-[100px] bg-[#212425]">
              <div className="flex items-center relative gap-1.5">
                <p className="text-sm font-semibold text-left text-white">
                  Months
                </p>
              </div>
            </button>
          </div>
        </div>

        <div className="flex items-center relative gap-2.5 p-1">
          <p className="flex-grow w-[262px] text-xs text-left text-[#6b6d6e]">
            Current Voting Power: {currentVotingPower}
          </p>
        </div>

        {amount && (
          <div className="flex items-center relative gap-2.5 p-1">
            <p className="flex-grow w-[262px] text-xs text-left text-[#6b6d6e]">
              Potential Voting Power: {totalVotingPower}
            </p>
          </div>
        )}

        <Button
          loading={isPending}
          disabled={isLoading || isPending || !amount}
          type="primary"
          onClick={handleConfirm}
        >
          Stake
        </Button>
      </div>

      {isOpen &&
        createPortal(<GovenModal onDismiss={() => setIsOpen(false)} />, document.body)}
    </>
  )
}